// Copyright (c) 2026 Aravind Adari. All rights reserved.

/**
 * Performance logger for monitoring Firestore queries and user actions.
 * Logs to the browser console with color-coded output.
 *
 * Usage:
 *   const snap = await timed("ReceptionistPage.load.branches", () => getDocs(col("branches")));
 *
 *   const end = perfMark("AdminPage.saveEmployee");
 *   await updateDoc(...);
 *   end();
 */

const COLOR_QUERY = "color:#1565C0;font-weight:bold";   // blue  — data loads
const COLOR_ACTION = "color:#2E7D32;font-weight:bold";  // green — user actions
const COLOR_TOTAL = "color:#0D47A1;font-weight:bold;font-size:13px"; // dark blue — totals
const COLOR_ERROR = "color:#C62828;font-weight:bold";   // red   — failures

/** Wrap a promise-returning function and log its duration. */
export async function timed<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const t0 = performance.now();
  try {
    const result = await fn();
    const ms = (performance.now() - t0).toFixed(0);
    console.log(`%c[Perf] ${label}: ${ms}ms`, COLOR_QUERY);
    return result;
  } catch (err) {
    const ms = (performance.now() - t0).toFixed(0);
    console.warn(`%c[Perf] ${label} FAILED after ${ms}ms`, COLOR_ERROR, err);
    throw err;
  }
}

/** Start a timer for a user action; call the returned function when done. */
export function perfMark(label: string): () => void {
  const t0 = performance.now();
  return () => {
    const ms = (performance.now() - t0).toFixed(0);
    console.log(`%c[Perf] ${label}: ${ms}ms`, COLOR_ACTION);
  };
}

/** Log a total load time (for grouping parallel queries). */
export function perfTotal(label: string, startMs: number): void {
  const ms = (performance.now() - startMs).toFixed(0);
  console.log(`%c[Perf] ${label}: ${ms}ms`, COLOR_TOTAL);
}
